import emailjs from "@emailjs/browser";
import { useRef, useState } from "react";
import { FiSend, FiCheck } from "react-icons/fi";


const ContactForm = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    website: 'KRICO',
    name: '',
    mobile: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit invoked");
    setShowErrors(true);
    setIsSubmitting(true);

    // Validation: Only submit if all fields are filled
    if (!formData.name || !formData.mobile || !formData.email || !formData.message) {
      setIsSubmitting(false);
      return;
    }
    emailjs
      .sendForm('service_contact', 'template_krico', formRef.current, { publicKey: 'MVunj_wUHEJxLDXLe' })
      .then(
        () => {
          console.log('SUCCESS!');
          setSubmitSuccess(true);
          setIsSubmitting(false);
          setFormData({
            name: '',
            mobile: '',
            email: '',
            message: ''
          });
        },
        (error) => {
          console.log('FAILED...', error.text);
        },
      );
  };

  const inputClasses = (fieldName) => `
    w-full px-4 py-3 rounded-lg
    border ${showErrors && !formData[fieldName] ? 'border-red-300 dark:border-red-500' : 'border-slate-300 dark:border-slate-600'}
    bg-white dark:bg-slate-800
    text-slate-900 dark:text-slate-100
    placeholder-slate-400 dark:placeholder-slate-500
    focus:outline-none focus:ring-2 focus:ring-yellow-500
    transition-all duration-300
    transform hover:shadow-md focus:shadow-lg
    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
  `;

  const labelClasses = `
    block text-sm font-medium
    text-slate-700 dark:text-slate-200
    mb-2 transition-colors
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-aos="fade-up" ref={formRef}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Name Input */}
        {!submitSuccess && <div className="relative group">
          <label htmlFor="name" className={labelClasses}>
            Name <span className="text-yellow-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={inputClasses('name')}
            placeholder="John Doe"
            required
            disabled={isSubmitting}
          />
          {showErrors && !formData.name && (
            <p className="mt-1 text-sm text-red-500 dark:text-red-400">Name is required</p>
          )}
        </div>}

        {/* Mobile Input */}
        {!submitSuccess && <div className="relative group">
          <label htmlFor="mobile" className={labelClasses}>
            Mobile <span className="text-yellow-500">*</span>
          </label>
          <input
            type="tel"
            id="mobile"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            className={inputClasses('mobile')}
            placeholder="+91 9876543210"
            required
            disabled={isSubmitting}
            pattern="^(\+?[0-9]{1,5})?([7-9][0-9]{9})$"
            title="Please enter a valid mobile number"
          />
          {showErrors && !formData.mobile && (
            <p className="mt-1 text-sm text-red-500 dark:text-red-400">Mobile number is required</p>
          )}
        </div>}
      </div>

      {/* Email Input */}
      {!submitSuccess && <div className="relative group">
        <label htmlFor="email" className={labelClasses}>
          Email <span className="text-yellow-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={inputClasses('email')}
          placeholder="john.doe@example.com"
          required
          disabled={isSubmitting}
        />
        {showErrors && !formData.email && (
          <p className="mt-1 text-sm text-red-500 dark:text-red-400">Email is required</p>
        )}
      </div>}

      {/* Message Input */}
      {!submitSuccess && <div className="relative group">
        <label htmlFor="message" className={labelClasses}>
          Message <span className="text-yellow-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="4"
          className={`${inputClasses('message')} resize-none`}
          placeholder="How can we help you?"
          required
          disabled={isSubmitting}
        ></textarea>
        {showErrors && !formData.message && (
          <p className="mt-1 text-sm text-red-500 dark:text-red-400">Message is required</p>
        )}
      </div>}

      {/* Submit Button */}
      <div className="relative">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`
            w-full flex items-center justify-center
            px-6 py-3.5
            text-base font-medium
            rounded-lg text-white
            transition-all duration-300
            transform hover:scale-[1.02] hover:shadow-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500
            disabled:opacity-50 disabled:cursor-not-allowed
            ${submitSuccess
              ? 'bg-green-500 hover:bg-green-600'
              : 'bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-600'
            }
          `}
        >
          <div className="relative flex items-center">
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </>
            ) : submitSuccess ? (
              <>
                <FiCheck className="w-5 h-5 mr-2" />
                Message Sent!
              </>
            ) : (
              <>
                <FiSend className="w-5 h-5 mr-2" />
                Send Message
              </>
            )}
          </div>
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
